import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import InArticleAd from '../components/InArticleAd';
import API_BASE_URL from '../config';

function renderInline(text) {
  var parts = [];
  var regex = /\*\*(.+?)\*\*/g;
  var last = 0;
  var match;
  var idx = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(<strong key={'b' + idx}>{match[1]}</strong>);
    last = regex.lastIndex;
    idx++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/**
 * Parse flat content array into sections split by h2 headings.
 * Returns [{ heading: string|null, blocks: [{ type, text/items }] }]
 */
function parseSections(content) {
  var sections = [];
  var current = { heading: null, blocks: [] };

  var i = 0;
  while (i < content.length) {
    var line = content[i];
    if (line.startsWith('## ')) {
      if (current.blocks.length > 0 || current.heading) sections.push(current);
      current = { heading: line.replace('## ', ''), blocks: [] };
      i++;
    } else if (line.startsWith('### ')) {
      current.blocks.push({ type: 'h3', text: line.replace('### ', '') });
      i++;
    } else if (line.startsWith('- ')) {
      var items = [];
      while (i < content.length && content[i].startsWith('- ')) {
        items.push(content[i].replace(/^- /, ''));
        i++;
      }
      current.blocks.push({ type: 'ul', items: items });
    } else {
      current.blocks.push({ type: 'p', text: line });
      i++;
    }
  }
  if (current.blocks.length > 0 || current.heading) sections.push(current);
  return sections;
}

function ArticlePage() {
  var { slug } = useParams();
  var [article, setArticle] = useState(null);
  var [related, setRelated] = useState([]);
  var [loading, setLoading] = useState(true);
  var [progress, setProgress] = useState(0);

  useEffect(function () {
    setLoading(true);
    fetch(API_BASE_URL + '/api/articles/' + slug)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        setArticle(data.article || null);
        setRelated(data.related || []);
        setLoading(false);
      })
      .catch(function () { setLoading(false); });
  }, [slug]);

  useEffect(function () {
    function handleScroll() {
      var scrollTop = window.scrollY;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) setProgress(Math.min((scrollTop / docHeight) * 100, 100));
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return function () { window.removeEventListener('scroll', handleScroll); };
  }, []);

  if (loading) {
    return (
      <div className="guide-page">
        <div className="guide-narrow" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
          <p style={{ color: 'var(--text-muted)' }}>Loading article…</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="guide-page">
        <div className="guide-narrow">
          <h1 className="guide-title">Article Not Found</h1>
          <p className="guide-p">Sorry, we couldn't find that article.</p>
          <Link to="/blog" className="btn btn-primary">Back to Articles</Link>
        </div>
      </div>
    );
  }

  var wordCount = article.content.join(' ').split(/\s+/).length;
  var readTime = Math.max(1, Math.round(wordCount / 230));
  var sections = parseSections(article.content);
  var adInserted = false;

  return (
    <article className="guide-page">
      <div className="reading-progress" style={{ width: progress + '%' }} aria-hidden="true" />

      <SEO
        title={article.title}
        description={article.excerpt}
        path={'/blog/' + article.slug}
        image={article.image}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: article.title,
          description: article.excerpt,
          image: article.image ? [article.image] : undefined,
          datePublished: article.date,
          dateModified: article.date,
          author: { '@type': 'Organization', name: 'Anthroper', url: 'https://anthroper.com' },
          publisher: {
            '@type': 'Organization',
            name: 'Anthroper',
            url: 'https://anthroper.com',
            logo: { '@type': 'ImageObject', url: 'https://anthroper.com/logo-anthroper.svg' },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://anthroper.com/blog/' + article.slug },
        })}</script>
      </Helmet>

      {/* Header */}
      <header className="guide-header">
        <div className="guide-narrow">
          <Link to="/blog" className="guide-back">← All Articles</Link>
          <div className="guide-meta">
            <span className="article-tag">{article.tag}</span>
            <span className="guide-meta-sep">·</span>
            <span className="guide-date">{article.date}</span>
            <span className="guide-meta-sep">·</span>
            <span className="guide-read-time">{readTime} min read</span>
          </div>
          <h1 className="guide-title">{article.title}</h1>
          <p className="guide-excerpt">{article.excerpt}</p>
        </div>
      </header>

      {/* Hero image */}
      {article.image && (
        <div className="article-hero-full">
          <img src={article.image} alt={article.title} className="article-hero-full-img" />
          <div className="article-hero-full-fade" aria-hidden="true" />
        </div>
      )}

      {/* Content sections — alternating backgrounds */}
      {sections.map(function (section, si) {
        var isEven = si % 2 === 0;
        var needsAd = !adInserted && si === 1;
        if (needsAd) adInserted = true;

        return (
          <section key={si} className={'guide-section' + (isEven ? '' : ' guide-section--tinted')}>
            <div className="guide-narrow">
              {section.heading && <h2 className="guide-h2">{renderInline(section.heading)}</h2>}
              {needsAd && <InArticleAd />}
              {section.blocks.map(function (block, bi) {
                var key = si + '-' + bi;
                if (block.type === 'h3') return <h3 key={key} className="guide-h3">{renderInline(block.text)}</h3>;
                if (block.type === 'ul') {
                  return (
                    <ul key={key} className="guide-ul">
                      {block.items.map(function (item, li) {
                        return <li key={li}>{renderInline(item)}</li>;
                      })}
                    </ul>
                  );
                }
                return <p key={key} className="guide-p">{renderInline(block.text)}</p>;
              })}
            </div>
          </section>
        );
      })}

      {/* Back to top */}
      <section className="guide-section">
        <div className="guide-narrow" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-4) 0' }}>
          <button
            className="btn btn-secondary"
            onClick={function () { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            type="button"
            style={{ borderRadius: '100px' }}
          >↑ Back to top</button>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="guide-section guide-section--tinted">
          <div className="guide-narrow">
            <h2 className="guide-h2">More in {article.tag}</h2>
            <div className="article-related-grid">
              {related.map(function (r) {
                return (
                  <Link to={'/blog/' + r.slug} key={r.slug} className="article-related-card">
                    {r.image && <img src={r.image} alt={r.title} className="article-related-img" loading="lazy" />}
                    <div className="article-related-body">
                      <span className="article-date">{r.date}</span>
                      <h3>{r.title}</h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="guide-section">
        <div className="guide-narrow">
          <div className="guide-cta">
            <p>Want to go deeper on AI? Explore our free guides.</p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link to="/learn" className="btn btn-primary">AI Guides</Link>
              <Link to="/tools" className="btn btn-secondary">Free Tools</Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}

export default ArticlePage;
